import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, AppRichEditor } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  ErrorHandlingTypeEnum,
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  LiveLectureTypeEnum,
} from "../../enums";
import {
  CreateLiveLectureRequest,
  LIVE_LECTURE_SLOT_DURATIONS,
  ParsedError,
} from "../../interfaces";
import { applyServerFormErrors } from "../../services";
import { AccountState } from "../../store/slices/accountSlice";
import {
  useAddLiveLectureMutation,
  useDeleteLiveLectureMutation,
  useGetLiveLectureQuery,
  useUpdateLiveLectureMutation,
} from "../../store/api/liveLectureApi";
import { LiveLectureInvitationsPanel } from "./LiveLectureInvitationsPanel";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: () => void;
};

const toLocalInputValue = (date: Date) =>
  moment(date).format("YYYY-MM-DDTHH:mm");

export const AddEditPrivateLiveLectureModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isEdit = Boolean(id);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const { data: liveLecture } = useGetLiveLectureQuery(id as string, {
    skip: !id,
  });

  const [
    addLiveLecture,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddLiveLectureMutation();
  const [
    updateLiveLecture,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateLiveLectureMutation();
  const [
    deleteLiveLecture,
    { isLoading: isLoadingDelete, error: errorDelete, isError: isErrorDelete },
  ] = useDeleteLiveLectureMutation();

  const defaultStart = useMemo(() => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    return start;
  }, []);

  const initialValues = useMemo(
    () => ({
      title: liveLecture?.title || { en: "", ar: "" },
      description: liveLecture?.description || { en: "", ar: "" },
      startLocal: toLocalInputValue(
        liveLecture?.startAt ? new Date(liveLecture.startAt) : defaultStart,
      ),
      durationMinutes: liveLecture?.durationMinutes || 60,
    }),
    [liveLecture, defaultStart],
  );

  const syncedIdRef = useRef<string | null>(null);
  const modalWasOpenRef = useRef(false);

  const form = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", { displayName: "englishValue" }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", { displayName: "arabicValue" }),
        ),
      }),
      startLocal: Yup.string().required(
        t("isNotEmpty", { displayName: "startAt" }),
      ),
      durationMinutes: Yup.number()
        .min(1)
        .required(t("isNotEmpty", { displayName: "duration" })),
    }),
    onSubmit: async (values) => {
      const startAt = new Date(values.startLocal);
      const endAt = new Date(
        startAt.getTime() + Number(values.durationMinutes) * 60 * 1000,
      );

      const unchanged =
        isEdit &&
        liveLecture?.startAt &&
        Math.abs(startAt.getTime() - new Date(liveLecture.startAt).getTime()) <
          60000;

      if (!unchanged && startAt < new Date()) {
        setScheduleError(t("liveLectureStartInPast"));
        return;
      }
      setScheduleError(null);

      const payload: CreateLiveLectureRequest = {
        title: values.title,
        description: values.description,
        accessMode: LiveLectureAccessModeEnum.PRIVATE,
        type: LiveLectureTypeEnum.GROUP,
        teacherUserId: profile?.id,
        startAt,
        endAt,
        durationMinutes: Number(values.durationMinutes),
        price: 0,
        currency: "KWD",
      };

      try {
        if (isEdit && id) {
          await updateLiveLecture({ id, data: payload }).unwrap();
        } else {
          await addLiveLecture(payload).unwrap();
        }
        onClose();
      } catch {
        // Handled by error effect
      }
    },
  });

  useEffect(() => {
    if (!show) {
      syncedIdRef.current = null;
      modalWasOpenRef.current = false;
      return;
    }

    const justOpened = !modalWasOpenRef.current;
    modalWasOpenRef.current = true;

    if (id && liveLecture) {
      if (syncedIdRef.current === id) return;
      syncedIdRef.current = id;
      form.setValues({
        title: liveLecture.title,
        description: liveLecture.description || { en: "", ar: "" },
        startLocal: toLocalInputValue(new Date(liveLecture.startAt)),
        durationMinutes: liveLecture.durationMinutes,
      });
      return;
    }

    if (!id && justOpened) {
      syncedIdRef.current = null;
      setScheduleError(null);
      form.resetForm({ values: initialValues });
    }
  }, [show, id, liveLecture, initialValues]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingDelete && isErrorDelete) _error = errorDelete as ParsedError;

    if (_error?.type === ErrorHandlingTypeEnum.FORM && _error.error) {
      applyServerFormErrors(form, _error.error);
      const slotMessage =
        (typeof _error.error.startAt === "string"
          ? _error.error.startAt
          : null) ||
        (typeof _error.error.endAt === "string" ? _error.error.endAt : null) ||
        (typeof _error.error.durationMinutes === "string"
          ? _error.error.durationMinutes
          : null);
      if (slotMessage) setScheduleError(slotMessage);
    }
  }, [
    isLoadingAdd,
    isLoadingUpdate,
    isLoadingDelete,
    isErrorAdd,
    isErrorUpdate,
    isErrorDelete,
  ]);

  const onClose = () => {
    syncedIdRef.current = null;
    form.resetForm();
    setScheduleError(null);
    onCloseClick();
  };

  const onDeleteConfirm = async () => {
    if (!id) return;
    await deleteLiveLecture(id);
    setShowDeleteModal(false);
    onClose();
  };

  const descriptionErrors = form.errors.description as
    | { en?: string; ar?: string }
    | undefined;
  const descriptionTouched = form.touched.description as
    | { en?: boolean; ar?: boolean }
    | undefined;

  const hostName =
    profile?.name?.en || profile?.name?.ar || profile?.email?.email || "-";
  const isBusy = isLoadingAdd || isLoadingUpdate || isLoadingDelete;

  return (
    <React.Fragment>
      <Modal
        id="addEditPrivateLiveLecture"
        isOpen={show}
        toggle={onClose}
        centered
        size="lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
        >
          <ModalHeader className="p-3" toggle={onClose}>
            {isEdit
              ? t("editItem", { item: "meeting" })
              : t("addPrivateLiveLecture")}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <p className="text-muted small">{t("privateLiveLectureHint")}</p>

            <div className="mb-3">
              <Label className="form-label">{t("host")}</Label>
              <Input value={hostName} disabled />
            </div>

            <Row>
              <Col md={8}>
                <div className="mb-3">
                  <Label htmlFor="startLocal" className="form-label">
                    {t("startAt")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="startLocal"
                    name="startLocal"
                    type="datetime-local"
                    min={
                      isEdit &&
                      liveLecture?.startAt &&
                      new Date(liveLecture.startAt).getTime() < Date.now()
                        ? undefined
                        : toLocalInputValue(new Date())
                    }
                    value={form.values.startLocal}
                    onChange={(e) => {
                      form.handleChange(e);
                      setScheduleError(null);
                    }}
                    onBlur={form.handleBlur}
                    invalid={Boolean(
                      (form.touched.startLocal && form.errors.startLocal) ||
                        scheduleError,
                    )}
                  />
                  {form.touched.startLocal && form.errors.startLocal ? (
                    <FormFeedback type="invalid">
                      {form.errors.startLocal as string}
                    </FormFeedback>
                  ) : null}
                  {scheduleError ? (
                    <FormFeedback type="invalid" className="d-block">
                      {scheduleError}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="durationMinutes" className="form-label">
                    {t("duration")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="durationMinutes"
                    name="durationMinutes"
                    type="select"
                    value={form.values.durationMinutes}
                    onChange={form.handleChange}
                  >
                    {LIVE_LECTURE_SLOT_DURATIONS.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {minutes} {t("minutes")}
                      </option>
                    ))}
                  </Input>
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                id="title.en"
                name="title.en"
                value={form.values.title?.en}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={Boolean(
                  form.touched.title?.en && form.errors.title?.en,
                )}
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="title.ar" className="form-label">
                {t("titleAr")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={Boolean(
                  form.touched.title?.ar && form.errors.title?.ar,
                )}
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("descriptionEn")}</Label>
              <div
                className={
                  descriptionTouched?.en && descriptionErrors?.en
                    ? "rounded border border-danger"
                    : undefined
                }
              >
                <AppRichEditor
                  key={`private-desc-en-${id || "new"}`}
                  initialValue={form.values.description?.en || ""}
                  onChange={(data: string) => {
                    form.setFieldValue("description.en", data);
                  }}
                  onFocus={() => form.setFieldTouched("description.en", true)}
                />
              </div>
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("descriptionAr")}</Label>
              <AppRichEditor
                key={`private-desc-ar-${id || "new"}`}
                initialValue={form.values.description?.ar || ""}
                onChange={(data: string) => {
                  form.setFieldValue("description.ar", data);
                }}
                onFocus={() => form.setFieldTouched("description.ar", true)}
              />
            </div>

            {isEdit &&
              id &&
              liveLecture?.status !== LiveLectureStatusEnum.CANCELLED && (
                <LiveLectureInvitationsPanel liveLectureId={id} />
              )}
          </ModalBody>
          <ModalFooter>
            <Link
              to="#"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            {isEdit && (
              <ActivityButton
                onClick={() => setShowDeleteModal(true)}
                title="delete"
                inProgressTitle="deleting"
                color="danger"
                className="btn btn-danger"
                type="button"
                inProgress={isLoadingDelete}
                disabled={isBusy}
              />
            )}
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isBusy}
              disabled={isBusy}
            />
          </ModalFooter>
        </Form>
      </Modal>
      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => setShowDeleteModal(false)}
      />
    </React.Fragment>
  );
};
